import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Navbar from "./Navbar";
import "./SignIn.css";

/* ================= API BASE ================= */
const normalizeBase = (value) =>
  String(value || "").trim().replace(/\/+$/, "");

const ensureApiSuffix = (base) => {
  const clean = normalizeBase(base);

  if (!clean) return "http://localhost:5000/api";

  return clean.endsWith("/api") ? clean : `${clean}/api`;
};

const API_BASE = ensureApiSuffix(
  process.env.REACT_APP_API_BASE || "http://localhost:5000"
);

/* ================= STORAGE KEYS ================= */
const AUTH_TOKEN_KEYS = [
  "token",
  "authToken",
  "accessToken",
  "userToken",
  "jwtToken",
];

const AUTH_USER_KEYS = [
  "user",
  "currentUser",
  "fullName",
  "name",
  "userName",
  "username",
  "email",
  "userEmail",
  "user_email",
  "isLoggedIn",
];

/* ================= AUTH HELPERS ================= */
const getStorageValue = (key) => {
  return localStorage.getItem(key) || sessionStorage.getItem(key) || "";
};

const getToken = () => {
  for (const key of AUTH_TOKEN_KEYS) {
    const value = getStorageValue(key);

    if (value && String(value).trim()) {
      return String(value).trim();
    }
  }

  return "";
};

const clearAuthStorage = () => {
  [...AUTH_TOKEN_KEYS, ...AUTH_USER_KEYS].forEach((key) => {
    localStorage.removeItem(key);
    sessionStorage.removeItem(key);
  });
};

const decodeJwtPayload = (token) => {
  try {
    if (!token || !token.includes(".")) return null;

    const base64Url = token.split(".")[1];

    if (!base64Url) return null;

    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const paddedBase64 = base64.padEnd(
      base64.length + ((4 - (base64.length % 4)) % 4),
      "="
    );

    const jsonPayload = decodeURIComponent(
      atob(paddedBase64)
        .split("")
        .map((char) => {
          return `%${`00${char.charCodeAt(0).toString(16)}`.slice(-2)}`;
        })
        .join("")
    );

    return JSON.parse(jsonPayload);
  } catch {
    return null;
  }
};

const isTokenValid = (token) => {
  const payload = decodeJwtPayload(token);

  if (!payload) return false;

  if (!payload.exp) return true;

  return payload.exp * 1000 > Date.now();
};

const notifyAuthChanged = (action = "auth-update") => {
  window.dispatchEvent(
    new CustomEvent("auth-state-changed", {
      detail: {
        source: "SignIn",
        action,
      },
    })
  );

  window.dispatchEvent(
    new CustomEvent("login-success", {
      detail: {
        source: "SignIn",
        action,
      },
    })
  );
};

const saveAuthData = ({ token, user }) => {
  clearAuthStorage();

  localStorage.setItem("token", token);
  localStorage.setItem("authToken", token);
  localStorage.setItem("accessToken", token);
  localStorage.setItem("isLoggedIn", "true");

  if (user && typeof user === "object") {
    localStorage.setItem("user", JSON.stringify(user));
    localStorage.setItem("currentUser", JSON.stringify(user));

    if (user.fullName) {
      localStorage.setItem("fullName", user.fullName);
      localStorage.setItem("name", user.fullName);
      localStorage.setItem("userName", user.fullName);
    }

    if (user.email) {
      localStorage.setItem("email", user.email);
      localStorage.setItem("userEmail", user.email);
      localStorage.setItem("user_email", user.email);
    }
  }
};

const getRedirectPath = (location) => {
  const from = location?.state?.from;

  if (from && typeof from === "string" && from !== "/signin") {
    return from;
  }

  return "/dashboard";
};

/* ================= COMPONENT ================= */
function SignIn() {
  const navigate = useNavigate();
  const location = useLocation();

  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });

  const [loading, setLoading] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [showResend, setShowResend] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);

  /* ================= AUTO REDIRECT ================= */
  useEffect(() => {
    const token = getToken();

    if (token && isTokenValid(token)) {
      localStorage.setItem("isLoggedIn", "true");
      navigate(getRedirectPath(location), { replace: true });
      return;
    }

    setCheckingAuth(false);
  }, [location, navigate]);

  /* ================= INPUT ================= */
  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    setError("");
    setMessage("");
    setShowResend(false);
  };

  /* ================= LOGIN ================= */
  const handleSubmit = async (e) => {
    e.preventDefault();

    setError("");
    setMessage("");
    setShowResend(false);

    const email = formData.email.trim().toLowerCase();
    const password = formData.password;

    if (!email || !password) {
      setError("Email and password are required.");
      return;
    }

    try {
      setLoading(true);

      const res = await fetch(`${API_BASE}/auth/signin`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email, password }),
      });

      const data = await res.json().catch(() => ({}));

      if (res.status === 403) {
        setError(data?.message || "Please verify your email first.");

        if (
          String(data?.message || "")
            .toLowerCase()
            .includes("verify")
        ) {
          setShowResend(true);
        }

        return;
      }

      if (!res.ok || !data?.success) {
        setError(data?.message || "Login failed.");
        return;
      }

      const token = data?.token;

      if (!token || !isTokenValid(token)) {
        setError("Invalid authentication token received.");
        return;
      }

      saveAuthData({
        token,
        user: data?.data || {},
      });

      notifyAuthChanged("login");

      setMessage("Login successful. Redirecting...");

      setTimeout(() => {
        navigate(getRedirectPath(location), { replace: true });
      }, 150);
    } catch (err) {
      console.error(err);
      setError("Server error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  /* ================= RESEND ================= */
  const handleResend = async () => {
    const email = formData.email.trim().toLowerCase();

    if (!email) {
      setError("Please enter your email first.");
      return;
    }

    try {
      setResendLoading(true);
      setError("");
      setMessage("");

      const res = await fetch(`${API_BASE}/auth/resend-verification`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email }),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok || data?.success === false) {
        setError(data?.message || "Failed to resend verification email.");
        return;
      }

      setMessage("Verification email sent again. Please check your inbox.");
    } catch {
      setError("Server error while resending verification email.");
    } finally {
      setResendLoading(false);
    }
  };

  if (checkingAuth) {
    return (
      <>
        <Navbar />
        <section className="signin-section">
          <div className="signin-container">
            <div className="signin-card">
              <h2>Checking session...</h2>
            </div>
          </div>
        </section>
      </>
    );
  }

  /* ================= UI ================= */
  return (
    <>
      <Navbar />

      <section className="signin-section">
        <div className="signin-container">
          <div className="signin-left">
            <h1>Welcome Back 👋</h1>
            <p>Login to manage your domains, hosting, payments and account.</p>
          </div>

          <div className="signin-card">
            <h2>Sign In</h2>

            {error ? <div className="auth-alert auth-error">{error}</div> : null}
            {message ? (
              <div className="auth-alert auth-success">{message}</div>
            ) : null}

            <form onSubmit={handleSubmit} className="signin-form">
              <div className="signin-input-group">
                <input
                  type="email"
                  name="email"
                  placeholder="Email"
                  value={formData.email}
                  onChange={handleChange}
                  disabled={loading}
                  autoComplete="email"
                />
              </div>

              <div className="signin-input-group">
                <input
                  type="password"
                  name="password"
                  placeholder="Password"
                  value={formData.password}
                  onChange={handleChange}
                  disabled={loading}
                  autoComplete="current-password"
                />
              </div>

              <div className="forgot-password-link">
                <Link to="/forgot-password">Forgot Password?</Link>
              </div>

              <button type="submit" className="signin-btn" disabled={loading}>
                {loading ? "Signing In..." : "Sign In"}
              </button>
            </form>

            {showResend ? (
              <button
                type="button"
                className="resend-btn"
                onClick={handleResend}
                disabled={resendLoading || loading}
              >
                {resendLoading ? "Sending..." : "Resend Verification Email"}
              </button>
            ) : null}

            <div className="signin-extra">
              <span>Don&apos;t have an account?</span>
              <Link to="/signup">Create Account</Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

export default SignIn;